import { type SourceType, isAudioMime } from "@ando/core/domain";
import {
  Composer,
  type FileChip,
  type MessageBodyCitationRef,
  type MessageCitationSourceType,
  type MessageScopeChip,
  MessageScopeChips,
  type MessageToolbarAction,
  Message as MessageView,
} from "@ando/ui";
import { Trans, useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import { Clock3 } from "lucide-react";
import * as React from "react";

import { formatDateTime, formatRelativeTime } from "@/lib/datetime";

import { generatedImageDocId, generatedSvgDocId } from "../generatedMedia";
import { selectPinnedMessages, useSidebarStore } from "../state/sidebarStore";
import type { ChatMessageView } from "../types";
import { ChatFileAudio } from "./ChatFileAudio";
import { ChatImageThumbnails } from "./ChatImageThumbnails";
import { MessageClarification } from "./MessageClarification";
import { MessageBusinessDefinition } from "./MessageBusinessDefinition";
import { MessagePreviewEmbed } from "./MessagePreviewEmbed";
// May 31, 2026 — Structured-data results no longer render inline in the
// chat row. They live exclusively in the work-area panel (dashboard /
// technical) and are re-entered via the chip strip surfaced just below
// the message (``DisplayResultChips`` mounted by the workspace shell).
// Mirror of v1's "displays = work-area first-class" intent but cleaner:
// the chat row stays a pure conversation thread.

const EMPTY_ACTIVE_ACTIONS: ReadonlyArray<MessageToolbarAction> = [];

// Stable per-kind action sets (referential identity → MessageView memo holds).
const AGENT_ACTIONS: MessageToolbarAction[] = [
  "feedback-up",
  "feedback-down",
  "feedback-heart",
  "copy",
  "retry",
  "branch",
  "pin",
];
// ``share`` sits after ``copy`` only on answers that are a deliverable —
// a result, a generated artefact, a cited synthesis, a long text. Sharing
// "Buongiorno! Come posso aiutarti?" would make the action read as noise.
const AGENT_ACTIONS_SHAREABLE: MessageToolbarAction[] = [
  ...AGENT_ACTIONS.slice(0, 4),
  "share",
  ...AGENT_ACTIONS.slice(4),
];
const SHAREABLE_MIN_CHARS = 400;
const USER_ACTIONS: MessageToolbarAction[] = ["edit", "copy"];
const NO_ACTIONS: MessageToolbarAction[] = [];

function isShareableAnswer(m: ChatMessageView): boolean {
  if (m.status && m.status !== "sent") return false;
  const displays = m.metadata?.displays;
  return Boolean(
    (m.citations && m.citations.length > 0) ||
      m.generatedImage ||
      m.generatedSvg ||
      m.inlineChart ||
      (Array.isArray(displays) && displays.length > 0) ||
      m.content.trim().length >= SHAREABLE_MIN_CHARS,
  );
}

function agentActionsFor(m: ChatMessageView): MessageToolbarAction[] {
  return isShareableAnswer(m) ? AGENT_ACTIONS_SHAREABLE : AGENT_ACTIONS;
}

/** Map the persisted feedback score (0/1/2) to its toolbar action. */
function feedbackToAction(feedback: number | undefined): MessageToolbarAction | null {
  if (feedback === 1) return "feedback-up";
  if (feedback === 0) return "feedback-down";
  if (feedback === 2) return "feedback-heart";
  return null;
}

/**
 * The wire hands us a raw ISO string and `MessageMeta` renders whatever
 * it is given — so the bubble used to show
 * `2026-08-26T19:24:32.287345+00:00` verbatim. Relative label in the
 * active locale ("9 ore fa"), exact date+time in the native tooltip.
 * `long` style: under a message the label reads as prose, not as the
 * console's compact chip.
 */
function MessageTimestamp({ iso, locale, responseDurationMs, retrievalEngine }: {
  iso?: string;
  locale: string;
  responseDurationMs?: number;
  retrievalEngine?: string;
}) {
  if (!iso) return null;
  const relative = formatRelativeTime(locale, iso, Date.now(), "long");
  if (relative === "—") return null;
  const seconds = responseDurationMs === undefined
    ? null
    : Math.max(0, Math.round(responseDurationMs / 1000));
  const duration = seconds === null
    ? null
    : seconds < 60
      ? `${seconds}s`
      : `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, "0")}s`;
  return (
    <details className="group relative text-[10px]">
      <summary
        aria-label={formatDateTime(locale, iso)}
        title={formatDateTime(locale, iso)}
        className="flex h-6 w-6 cursor-pointer list-none items-center justify-center rounded-md text-ink-3 hover:text-ink-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ando-orange [&::-webkit-details-marker]:hidden"
      >
        <Clock3 size={12} strokeWidth={1.5} aria-hidden />
      </summary>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1 text-ink-3">
        <time dateTime={iso} title={formatDateTime(locale, iso)} className="whitespace-nowrap">
          {relative}
        </time>
        {duration && (
          <span title={`${responseDurationMs} ms`} className="whitespace-nowrap">
            <Trans>Risposta in</Trans> {duration}
          </span>
        )}
        {retrievalEngine === "trama" && <span className="whitespace-nowrap"><Trans>TRAMA β</Trans></span>}
      </div>
    </details>
  );
}

/**
 * Bridge type alias — `@ando/ui` re-declares `MessageCitationSourceType`
 * locally (UI is a leaf package, can't depend on `@ando/core`). The two
 * unions are intentionally identical at the value-level — keep them in
 * sync if `SourceType` ever grows.
 */
type _UiTypeMatchesDomain = SourceType extends MessageCitationSourceType
  ? MessageCitationSourceType extends SourceType
    ? true
    : false
  : false;
const _typeBridge: _UiTypeMatchesDomain = true;

/**
 * Messages — scrollable list renderer for `ChatMessageView[]`.
 *
 * Virtualization decision (F2.B):
 *   - Typical Ando conversations stay under ~200 turns; bubbles have
 *     variable height (markdown, tables, code blocks). `@tanstack/react-virtual`
 *     would force per-row measurement and a `position: absolute` body that
 *     breaks Framer Motion `layout` animations + accessible focus order.
 *   - Decision: **render flat** by default. The `virtualized` prop is a
 *     placeholder for a future opt-in (e.g. archive / search views with
 *     thousands of rows). Until that surface ships, virtualization is
 *     deferred to avoid premature optimization.
 *
 * Auto-scroll:
 *   - When a new message arrives AND the user is within `STICKY_PX` of the
 *     bottom, scroll smoothly down.
 *   - When the user has scrolled up beyond that threshold, freeze position
 *     and surface a "↓ Nuovi messaggi" pill (rendered by the consumer via
 *     `renderNewMessagesPill`). The pill is mounted as a sibling of the
 *     scroll container inside a `relative` wrapper, so consumers can anchor
 *     it to the visible viewport with `absolute bottom-X` — NOT to the end
 *     of the scrollable content (which would float off-screen when the user
 *     scrolls up).
 */

const STICKY_PX = 100;

export interface MessagesProps {
  messages: ChatMessageView[];
  onCitationClick?: (sourceId: string, sourceType?: SourceType) => void;
  /** Override when you want full virtualization (placeholder — see decision above). */
  virtualized?: boolean;
  /** Render slot for the floating "new messages" pill. */
  renderNewMessagesPill?: (props: { onJumpToBottom: () => void }) => React.ReactNode;
  /**
   * Render slot mounted after the last message and BEFORE the bottom
   * scroll-anchor. Used by the parent to inject a `<StreamingIndicator>`
   * while Ando is processing the current query. Kept as a slot (not a
   * store-coupled hook) so `<Messages>` stays presentational and trivially
   * testable without the chat store.
   */
  streamingSlot?: React.ReactNode;
  /**
   * Per-message toolbar click handler. Receives the action token + the
   * message view so the parent can route on (kind, id) without a second
   * lookup. ``Messages`` stays presentational and never owns the side-
   * effect itself.
   */
  onToolbarAction?: (action: MessageToolbarAction, message: ChatMessageView) => void;
  /**
   * Render slot inserted AFTER each message's body — used by the parent
   * to attach contextual chrome (e.g. the ``SourceRailSurface`` chip row
   * that lets the user re-open the citations of a historical AI answer
   * once the auto-open viewer has been dismissed). The slot lives inside
   * the same flex-col as the message bubble so it scrolls in lock-step.
   * Render-prop kept presentational: ``Messages`` itself never imports
   * sources — that would cycle features/chat ↔ features/sources.
   */
  renderAfterMessage?: (message: ChatMessageView) => React.ReactNode;
  className?: string;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/**
 * Resolve the retrieval engine the BE stamped on an agent answer
 * (``metadata.retrieval_engine`` — e.g. ``"terra"`` for the TERRA β
 * experimental relational-RAG engine, Jul 2026).
 *
 * ``ChatMessageView.metadata`` is the raw ``wirePayload`` bag, whose shape
 * differs by provenance:
 *   - live WS ``answer`` frame → ``wirePayload = envelope.payload`` →
 *     the engine lives at ``metadata.retrieval_engine``;
 *   - history rebuild → ``wirePayload = whole frame`` → it lives at
 *     ``payload.metadata.retrieval_engine``.
 * Checked in that order; ``undefined`` for user/system turns and legacy
 * answers (graceful — the badge simply doesn't render).
 */
function retrievalEngineOf(m: ChatMessageView): string | undefined {
  const bag = m.metadata;
  if (!isRecord(bag)) return undefined;
  const live = bag.metadata;
  if (isRecord(live) && typeof live.retrieval_engine === "string") {
    return live.retrieval_engine;
  }
  const frame = bag.payload;
  if (
    isRecord(frame) &&
    isRecord(frame.metadata) &&
    typeof frame.metadata.retrieval_engine === "string"
  ) {
    return frame.metadata.retrieval_engine;
  }
  return undefined;
}

function citationsOf(m: ChatMessageView): MessageBodyCitationRef[] | undefined {
  if (!m.citations?.length) return undefined;
  return m.citations.map((c, i) => ({
    index: i + 1,
    sourceId: c.id,
    // Propagate the wire ``source_type`` so ``useCitationClick`` can pick
    // the right drawer kind (email vs PDF vs DB vs web). ``MessageBody``
    // forwards this value into the ``CitationClickHandler``.
    sourceType: c.source_type,
    title: c.title ?? c.source_file,
    // Jun 2026 — forward the inline metadata the wire already carries
    // so web citations can skip the BE resolver (root cause of the
    // "Documento non disponibile" placeholder before this fix). The
    // BE ``ChatCitation`` shape populates ``url`` for web sources;
    // for non-web it stays undefined and the resolver path runs.
    url: c.url,
    citationTitle: c.title ?? c.source_file,
    snippet: c.snippet,
  }));
}

export const Messages: React.FC<MessagesProps> = ({
  messages,
  onCitationClick,
  virtualized: _virtualized = false,
  renderNewMessagesPill,
  streamingSlot,
  onToolbarAction,
  renderAfterMessage,
  className,
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const pinnedMessages = useSidebarStore(selectPinnedMessages);
  const navigate = useNavigate();
  const { i18n } = useLingui();

  // Per-message "active" toolbar state = the given feedback (filled thumb /
  // heart) + the pin toggle, combined. Built once per messages/pin change so
  // the array identity is stable across unrelated re-renders (MessageView is
  // memoized on its props). Messages with no active state fall back to the
  // shared EMPTY constant at read time, so we only allocate for the few that
  // are rated or pinned.
  const activeActionsById = React.useMemo(() => {
    const map: Record<string, ReadonlyArray<MessageToolbarAction>> = {};
    for (const m of messages) {
      const fb = feedbackToAction(m.feedback);
      const pinned = Boolean(pinnedMessages[m.id]);
      if (!fb && !pinned) continue;
      const arr: MessageToolbarAction[] = [];
      if (fb) arr.push(fb);
      if (pinned) arr.push("pin");
      map[m.id] = arr;
    }
    return map;
  }, [messages, pinnedMessages]);
  // Both timestamps are already persisted in `messages` and share queryId.
  // Deriving the elapsed time here also works for older turns after reload.
  const responseDurationById = React.useMemo(() => {
    const promptTimeByQuery = new Map<string, number>();
    const durations = new Map<string, number>();
    for (const message of messages) {
      if (!message.queryId || !message.timestamp) continue;
      const time = Date.parse(message.timestamp);
      if (!Number.isFinite(time)) continue;
      if (message.kind === "user") promptTimeByQuery.set(message.queryId, time);
      if (message.kind === "agent" && message.status === "sent") {
        const started = promptTimeByQuery.get(message.queryId);
        if (started !== undefined && time >= started) durations.set(message.id, time - started);
      }
    }
    return durations;
  }, [messages]);
  const [stuckToBottom, setStuckToBottom] = React.useState(true);
  const stuckToBottomRef = React.useRef(true);

  // Track scroll position — when the user scrolls up by >STICKY_PX, we
  // freeze the auto-scroll and surface the "new messages" pill.
  const onScroll = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stuckToBottomRef.current = distanceFromBottom < STICKY_PX;
    setStuckToBottom(stuckToBottomRef.current);
  }, []);

  const scrollToBottom = React.useCallback((behavior: ScrollBehavior = "smooth") => {
    bottomRef.current?.scrollIntoView({ behavior, block: "end" });
  }, []);

  const lastMessage = messages.at(-1);
  const lastContentLength = lastMessage?.content.length ?? 0;
  const previousContent = React.useRef({ id: lastMessage?.id, length: lastContentLength });
  React.useLayoutEffect(() => {
    const previous = previousContent.current;
    if (
      lastMessage?.status === "streaming" &&
      previous.id === lastMessage.id &&
      lastContentLength > previous.length &&
      stuckToBottomRef.current
    ) {
      scrollToBottom("auto");
    }
    previousContent.current = { id: lastMessage?.id, length: lastContentLength };
  }, [lastMessage?.id, lastMessage?.status, lastContentLength, scrollToBottom]);

  // Auto-scroll on new messages, only when already stuck to bottom.
  const lastLen = React.useRef(messages.length);
  React.useEffect(() => {
    if (messages.length > lastLen.current && stuckToBottom) {
      scrollToBottom("smooth");
    }
    lastLen.current = messages.length;
  }, [messages.length, stuckToBottom, scrollToBottom]);

  // Streaming indicator visibility shifts the bottom — keep it in view when
  // the user is already pinned to the bottom (so the dots / blob bubble
  // never appear off-screen the moment they mount).
  const hadStreamingSlot = React.useRef(Boolean(streamingSlot));
  React.useEffect(() => {
    const hasSlot = Boolean(streamingSlot);
    if (hasSlot && !hadStreamingSlot.current && stuckToBottom) {
      scrollToBottom("smooth");
    }
    hadStreamingSlot.current = hasSlot;
  }, [streamingSlot, stuckToBottom, scrollToBottom]);

  // First mount: snap to bottom without animation. `scrollToBottom` is a
  // stable `useCallback([])` so the empty deps is correct (and is what we
  // want — we explicitly only fire on initial mount, never on rerender).
  const didMount = React.useRef(false);
  React.useEffect(() => {
    if (didMount.current) return;
    didMount.current = true;
    scrollToBottom("auto");
  }, [scrollToBottom]);

  // A clarification selection is a transport retry, not a new question the
  // user typed. Match the persisted retry to the card that offered it so the
  // conversation remains readable after refresh as well as optimistically.
  const clarificationSelections = new Map<string, string>();
  const businessDefinitionAnswers = new Map<string, string>();
  const clarificationRetryIds = new Set<string>();
  for (let index = 0; index + 1 < messages.length; index += 1) {
    const card = messages[index];
    const retry = messages[index + 1];
    if (!card || !retry || card.kind !== "agent" || retry.kind !== "user") continue;
    if (card.businessDefinitionNeeded) {
      businessDefinitionAnswers.set(card.id, retry.content.trim());
    }
    const choice = card.clarification?.questions.find(
      (option) => option.suggested_query?.trim() === retry.content.trim(),
    );
    if (!choice) continue;
    clarificationSelections.set(card.id, retry.content.trim());
    clarificationRetryIds.add(retry.id);
  }

  return (
    <div className={`relative flex h-full min-h-0 flex-col ${className ?? ""}`}>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="chat-msgs flex h-full flex-col gap-7 overflow-y-auto px-8 pt-20 pb-8"
      >
        {messages.filter((m) => !clarificationRetryIds.has(m.id)).map((m) => (
          <React.Fragment key={m.id}>
            {streamingSlot && m.id === lastMessage?.id && m.kind === "agent" && m.status === "streaming" ? (
              <div className="-mb-2 flex">{streamingSlot}</div>
            ) : null}
            <MessageView
              kind={m.kind}
              markdown={m.content}
              // Only the AGENT's own label is dropped: one speaker per
              // side (brand mark + orange bubble) made "ANDO" noise.
              // Any other author still renders — the day the wire carries
              // a real name (shared conversation, multi-user room) it
              // shows up instead of being swallowed here. See the author
              // row in `ConnectMessageThread`, where names are load-bearing.
              author={m.kind === "agent" ? undefined : m.author}
              timestamp={
                <MessageTimestamp
                  iso={m.timestamp}
                  locale={i18n.locale}
                  responseDurationMs={responseDurationById.get(m.id)}
                  retrievalEngine={m.kind === "agent" ? retrievalEngineOf(m) : undefined}
                />
              }
              status={m.status}
              systemLabel={m.systemLabel}
              citations={citationsOf(m)}
              onCitationClick={onCitationClick}
              toolbarActions={
                m.kind === "agent" ? agentActionsFor(m) : m.kind === "user" ? USER_ACTIONS : NO_ACTIONS
              }
              onToolbarAction={onToolbarAction ? (action) => onToolbarAction(action, m) : undefined}
              activeToolbarActions={activeActionsById[m.id] ?? EMPTY_ACTIVE_ACTIONS}
            />
            {/* User-uploaded chat images — thumbnail row under the
                user bubble. Without this, the BE persists+returns the
                attachment but the FE never showed it, so users
                opening a past "Cosa vedi?" turn saw the agent's
                description with no visual context (Jun 23, 2026
                fix). Each thumbnail pre-fetches its MinIO blob via
                an auth-bearing helper — see
                ``ChatImageThumbnails.tsx``. */}
            {m.kind === "user" && m.imageAttachments?.length ? (
              <ChatImageThumbnails attachments={m.imageAttachments} />
            ) : null}
            {/* Voice-note attachments — render under the user bubble so
                the audio scrubber sits between the message text and the
                next row. Filtered by MIME so non-audio files (PDF /
                docx) keep using the existing chip surface. */}
            {m.kind === "user" && m.fileAttachments?.length
              ? m.fileAttachments
                  .filter((f) => isAudioMime(f.mime_type))
                  .map((f) => (
                    <div key={`audio-${f.content_id}`} className="-mt-2 flex justify-end">
                      <ChatFileAudio contentId={f.content_id} filename={f.filename} />
                    </div>
                  ))
              : null}
            {/* NON-audio attachments (PDF / docx / xlsx / …) — render as a
                READ-ONLY chip row under the user bubble so the file the user
                attached is visible in the conversation (V1 parity — it was
                missing in V2). Reuses the composer's ``FileChipList`` with no
                ``onRemove`` (sent message → no remove button). */}
            {(() => {
              if (m.kind !== "user" || !m.fileAttachments?.length) return null;
              const docs = m.fileAttachments.filter((f) => !isAudioMime(f.mime_type));
              if (docs.length === 0) return null;
              const chips: FileChip[] = docs.map((f) => ({
                key: f.content_id,
                filename: f.filename,
                status: f.ingestion_status,
              }));
              return (
                <div className="-mt-2 flex justify-end">
                  <Composer.FileChipList files={chips} />
                </div>
              );
            })()}
            {/* Jun 2026 — scope mentions (SourceScopePicker + inline @-
                mentions) persisted on the user message at send time. Read-
                only chip row mirrors the upload pattern above so the user
                sees AT A GLANCE which files/folders were used to narrow
                retrieval for that turn (fixes "ho selezionato un file ma
                non si vede" feedback). */}
            {(() => {
              if (m.kind !== "user" || !m.mentions?.length) return null;
              const chips: MessageScopeChip[] = m.mentions.map((mt) => ({
                kind: mt.kind,
                id: mt.id,
                display: mt.display,
                provider: mt.provider,
                folderPath: mt.folderPath,
              }));
              return (
                <div className="-mt-2 flex justify-end">
                  <MessageScopeChips chips={chips} />
                </div>
              );
            })()}
            {/* Agent-generated media — image / SVG / inline chart. Replaces
                the prior flat ``MediaChip`` pills with a real preview embed
                (June 14, 2026 UX upgrade): the asset itself becomes the
                affordance instead of a tiny label, signalling "I made this
                for you" much more loudly. One polymorphic
                ``<MessagePreviewEmbed>`` handles all three kinds via a
                discriminated union — DRY shell, kind-specific click target. */}
            {/* Ambiguity the connector refused to guess through (Aug 2026).
                Sits FIRST among the agent sub-blocks: on a turn where the
                data question could not be answered, these options are the
                only actionable thing the user has — burying them under
                provenance chrome would be exactly backwards. Aligned to the
                agent body gutter like its siblings below. */}
            {m.kind === "agent" && m.clarification?.questions.length ? (
              <div className="-mt-4 max-w-[520px] pl-8">
                <MessageClarification
                  payload={m.clarification}
                  selectedQuery={clarificationSelections.get(m.id)}
                />
              </div>
            ) : null}
            {m.kind === "agent" && m.businessDefinitionNeeded ? (
              <div className="-mt-4 max-w-[520px] pl-8">
                <MessageBusinessDefinition
                  payload={m.businessDefinitionNeeded}
                  selectedAnswer={businessDefinitionAnswers.get(m.id)}
                />
              </div>
            ) : null}
            {/* Numeric consensus diagnostics lack claim context and answer impact.
                Keep them in provenance, not in the conversation UI. */}
            {m.kind === "agent" && m.generatedImage
              ? (() => {
                  const docId = generatedImageDocId(m.generatedImage);
                  if (!docId) return null;
                  return (
                    <MessagePreviewEmbed
                      kind="image"
                      payload={m.generatedImage}
                      onOpen={() =>
                        void navigate({
                          to: "/workspace/documents",
                          search: { focus: docId } as Record<string, unknown>,
                        })
                      }
                    />
                  );
                })()
              : null}
            {m.kind === "agent" && m.generatedSvg
              ? (() => {
                  const docId = generatedSvgDocId(m.generatedSvg);
                  if (!docId) return null;
                  return (
                    <MessagePreviewEmbed
                      kind="svg"
                      payload={m.generatedSvg}
                      onOpen={() =>
                        void navigate({
                          to: "/workspace/documents",
                          search: { focus: docId } as Record<string, unknown>,
                        })
                      }
                    />
                  );
                })()
              : null}
            {m.kind === "agent" && m.inlineChart ? (
              <MessagePreviewEmbed
                kind="chart"
                payload={m.inlineChart}
                onOpen={() =>
                  void navigate({
                    to: "/workspace/dashboard",
                    search: { inlineChart: m.id } as Record<string, unknown>,
                  })
                }
              />
            ) : null}
            {/* Voice-of-X report preview (Workstream B M2.4) — chip deep-
                links to the report viewer. We stash the report id on the
                ``documents`` working-area search params so the existing
                workspace router resolves it (the dedicated
                ``/workspace/reports/$reportId`` route lands in a later
                milestone; until then the documents tab is the host). */}
            {m.kind === "agent" && m.voiceReport ? (
              <MessagePreviewEmbed
                kind="voice_report"
                payload={m.voiceReport}
                onOpen={() =>
                  void navigate({
                    to: "/workspace/documents",
                    search: {
                      reportId: m.voiceReport?.report_id,
                    } as Record<string, unknown>,
                  })
                }
              />
            ) : null}
            {renderAfterMessage ? renderAfterMessage(m) : null}
          </React.Fragment>
        ))}
        {streamingSlot && !(lastMessage?.kind === "agent" && lastMessage.status === "streaming") ? (
          <div className="-mt-2 flex motion-safe:animate-msg-in">{streamingSlot}</div>
        ) : null}
        <div ref={bottomRef} aria-hidden />
      </div>
      {!stuckToBottom && renderNewMessagesPill
        ? renderNewMessagesPill({ onJumpToBottom: () => scrollToBottom() })
        : null}
    </div>
  );
};
Messages.displayName = "Messages";
